import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { MapPin, Maximize, ArrowRight, ArrowLeft } from 'lucide-react';
import p1 from '../assets/p1.webp';
import p2 from '../assets/p2.webp';
import p3 from '../assets/p3.webp';
import './Portfolio.css';

import { projectsApi } from '../api/projects';

const BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

export function Portfolio() {
    const { t, language } = useLanguage();
    const { search } = useLocation();
    const isRtl = language === 'ar';
    const [activeFilter, setActiveFilter] = useState('all');
    const [isLoading, setIsLoading] = useState(true);

    const filterOptions = [
        { id: 'all', label: t('portfolio.filters.all') },
        { id: 'construction', label: t('portfolio.filters.construction') },
        { id: 'interior', label: t('portfolio.filters.interior') },
        { id: 'facades', label: t('portfolio.filters.facades') },
        { id: 'finishing', label: t('portfolio.filters.finishing') },
    ];

    useEffect(() => {
        const params = new URLSearchParams(search);
        const filter = params.get('filter');
        if (filter && filterOptions.some(opt => opt.id === filter)) {
            setActiveFilter(filter);
        } else {
            setActiveFilter('all');
        }
    }, [search]);

    const [projectsData, setProjectsData] = useState([]);

    useEffect(() => {
        const loadProjects = async () => {
            setIsLoading(true);
            try {
                const data = await projectsApi.getAll();
                if (data && data.length > 0) {
                    setProjectsData(data.map((p, i) => ({
                        ...p,
                        image: (i % 3 === 0 ? p1 : i % 3 === 1 ? p2 : p3),
                        categoryKey: (p.category || '').toLowerCase()
                    })));
                } else {
                    throw new Error('No data');
                }
            } catch (err) {
                const saved = localStorage.getItem('site_projects_v2');
                if (saved) {
                    const parsed = JSON.parse(saved);
                    setProjectsData(parsed.map((p, i) => ({
                        ...p,
                        image: i % 3 === 0 ? p1 : i % 3 === 1 ? p2 : p3,
                        categoryKey: p.category.toLowerCase()
                    })));
                } else {
                    setProjectsData([
                        { id: 'p1', image: p1, categoryKey: 'interior', title: '', category: 'Interior', location: '' },
                        { id: 'p2', image: p2, categoryKey: 'facades', title: '', category: 'Facades', location: '' },
                        { id: 'p3', image: p3, categoryKey: 'interior', title: '', category: 'Interior', location: '' },
                    ]);
                }
            } finally {
                setIsLoading(false);
            }
        };
        loadProjects();
    }, []);

    const filteredProjects = activeFilter === 'all'
        ? projectsData
        : projectsData.filter(p => p.categoryKey === activeFilter);

    const getVal = (p, key) => {
        if (!isRtl) {
            const enMap = { title: 'titleEn', description: 'descriptionEn', location: 'locationEn' };
            if (enMap[key] && p[enMap[key]]) return p[enMap[key]];
        }
        return p[key] || '';
    };

    const getTranslatedCategory = (key, fallback) => {
        const translationMap = {
            'construction': t('portfolio.filters.construction'),
            'interior': t('portfolio.filters.interior'),
            'facades': t('portfolio.filters.facades'),
            'finishing': t('portfolio.filters.finishing')
        };
        return translationMap[key] || fallback;
    };

    if (isLoading) return (
        <div className="studio-loader">
            <div className="studio-spinner"></div>
        </div>
    );

    return (
        <main className="studio-page" dir={isRtl ? 'rtl' : 'ltr'}>
            
            {/* CLEAN LUXURY HERO - NO SHAPES */}
            <section className="studio-hero">
                <div className="studio-hero-content">
                    <div className="studio-badge-wrapper">
                        <div className="studio-hero-badge">
                            {/* MAKING HOME CLICKABLE */}
                            <Link to="/" className="badge-link">{t('nav.home')}</Link>
                            <span className="badge-separator"> / </span>
                            <span>{t('nav.projects')}</span>
                        </div>
                    </div>
                    <h1 className="studio-hero-title">{t('portfolio.title')}</h1>
                    <p className="studio-hero-desc">{t('portfolio.description')}</p>
                </div>
            </section>

            {/* PERFECTLY CENTERED GLASS BANNER */}
            <section className="studio-filter-section">
                <div className="studio-container">
                    <div className="studio-filter-wrapper">
                        <div className="studio-glass-pill">
                            {filterOptions.map(option => (
                                <button
                                    key={option.id}
                                    className={`studio-filter-btn ${activeFilter === option.id ? 'studio-active' : ''}`}
                                    onClick={() => setActiveFilter(option.id)}
                                >
                                    <span className="studio-btn-text">{option.label}</span>
                                    <div className="studio-btn-bg"></div>
                                </button>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* 3-COLUMN PROJECTS GRID */}
            <section className="studio-gallery">
                <div className="studio-container">
                    <div className="studio-results-header">
                        <div className="studio-header-line"></div>
                        <span className="studio-result-count">{t('portfolio.projectCount', { count: filteredProjects.length })}</span>
                        <div className="studio-header-line"></div>
                    </div>

                    <div className="studio-grid">
                        {filteredProjects.map((project) => (
                            <Link to={`/portfolio/${project.id}`} key={project.id} className="studio-card">
                                
                                <div className="studio-card-img-box">
                                    <img 
                                        src={(() => { 
                                            const cover = project.images?.find(i => i.isPrimary) || project.images?.[0]; 
                                            return cover ? (BASE_URL.replace('/api','') + cover.imageUrl) : project.image; 
                                        })()} 
                                        alt={getVal(project, 'title')} 
                                    />
                                    <div className="studio-card-overlay"></div>
                                    <div className="studio-gold-unveil"></div>
                                    <div className="studio-category-tag">
                                        {getTranslatedCategory(project.categoryKey, getVal(project, 'category'))}
                                    </div>
                                </div>

                                <div className="studio-card-content">
                                    <div className="studio-card-header">
                                        <h3 className="studio-card-title">{getVal(project, 'title')}</h3>
                                        <div className="studio-explore-btn">
                                            {isRtl ? <ArrowLeft size={18} /> : <ArrowRight size={18} />}
                                        </div>
                                    </div>
                                    <p className="studio-card-desc">{getVal(project, 'description')}</p>

                                    <div className="studio-card-meta">
                                        {(project.location || project.locationEn) && (
                                            <div className="studio-meta-item">
                                                <MapPin size={14} />
                                                <span>{(!isRtl && project.locationEn) ? project.locationEn : project.location}</span>
                                            </div>
                                        )}
                                        {project.area && (
                                            <div className="studio-meta-item">
                                                <Maximize size={14} />
                                                <span>{project.area}</span>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </Link>
                        ))}
                    </div>
                </div>
            </section>
        </main>
    );
}
